import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Row } from "@amzn/stencil-react-components/layout";
import { FormattedMessage } from "react-intl";

import { publishSecurityKeyUnavailableClickedMetrics } from "@/apis/Metrics";

import ReportSecurityKeyIssuesModal from "./ReportSecurityKeyIssuesModal";

/**
 * Props for the SecurityKeyActionButtons component
 */
interface SecurityKeyActionButtonsProps {
  /** Callback function for the primary action (insert security key) */
  onPrimaryAction: () => void;
  /** Text to display on the primary button. Defaults to "I have inserted a security key" */
  primaryButtonText?: string;
  /** Test ID for the primary button. Defaults to "security-key-timeout-btn" */
  primaryButtonTestId?: string;
  /** Whether to hide the "I don't have a security key" button */
  hideNoSecurityKeyButton?: boolean;
}

/**
 * Shared action buttons component for security key detection flow.
 * Renders a primary button for confirming security key insertion and a secondary button for reporting issues.
 *
 * @param props - The component props
 * @param props.onPrimaryAction - Callback function triggered when primary button is clicked
 * @param props.primaryButtonText - Text to display on primary button
 * @param props.primaryButtonTestId - Test ID for primary button
 * @param props.hideNoSecurityKeyButton - Whether to hide the "I don't have a security key" button
 *
 * @returns JSX element containing the action buttons
 */
export default function SecurityKeyActionButtons({
  onPrimaryAction,
  primaryButtonText,
  primaryButtonTestId = "security-key-timeout-btn",
  hideNoSecurityKeyButton = false,
}: SecurityKeyActionButtonsProps): JSX.Element {
  return (
    <Row columnGap="S300" justifyContent="flex-start">
      <Button
        variant={ButtonVariant.Primary}
        onClick={onPrimaryAction}
        dataTestId={primaryButtonTestId}
      >
        {primaryButtonText || (
          <FormattedMessage id="insertAndTestSecurityKey-actionButtons-primaryButtonText" />
        )}
      </Button>
      {!hideNoSecurityKeyButton && (
        <ReportSecurityKeyIssuesModal>
          {({ open }) => (
            <>
              <Button
                variant={ButtonVariant.Tertiary}
                onClick={() => {
                  publishSecurityKeyUnavailableClickedMetrics();
                  open();
                }}
                dataTestId="noSecurityKeyButton"
                aria-describedby="sroNoSecurityKeyDialog"
              >
                <FormattedMessage id="insertAndTestSecurityKey-actionButtons-noSecurityKeyButton" />
              </Button>
              <span hidden id="sroNoSecurityKeyDialog">
                <FormattedMessage id="insertAndTestSecurityKey-actionButtons-noSecurityKeyDialog" />
              </span>
            </>
          )}
        </ReportSecurityKeyIssuesModal>
      )}
    </Row>
  );
}
